/**
 * Portão de decisão do CASO e o estado que vem DEPOIS dele (E67.F1, E72.F2).
 *
 * Vivia dentro de `PreAnalisePage`; saiu para arquivo próprio quando ganhou a
 * terceira saída — a página já orquestra carga, auto-run, decisão e download, e
 * empilhar a apresentação do rodapé em cima disso é como os dois componentes da
 * revisão de assinaturas passaram do limite de tamanho (F-E71.F1-04).
 *
 * As três saídas são mutuamente exclusivas e terminais para a triagem: encerrar
 * sem documento, encerrar EMITINDO o Relatório Preliminar (E72.F1, ADR-0074) e
 * seguir para o exame pericial. Quem decide é o servidor, numa transação sobre
 * todas as triagens pendentes do caso; aqui só há botão.
 *
 * O portão SOME quando não há mais triagem pendente. Por isso o desfecho com
 * relatório tem bloco próprio: sem ele, o escritório geraria o documento e a
 * tela não teria mais onde oferecer o download.
 */
import { Download, FileText } from 'lucide-react';

import { ptBR } from '@/i18n/pt-BR';
import { Button } from '@/components/ui/Button';
import type { PreAnaliseAcao } from '@/lib/pre_analise';

export interface PortaoDoCasoProps {
  visivel: boolean;
  triadas: number;
  /** Questionadas vivas do caso — o denominador da cobertura. */
  total: number;
  /** A ação em voo, ou `null`. Só o botão acionado troca de rótulo. */
  acaoEmCurso: PreAnaliseAcao | null;
  travado: boolean;
  rodando: boolean;
  onAcao: (acao: PreAnaliseAcao) => void;
}

function resumoDoPortao(
  triadas: number,
  total: number,
  travado: boolean,
  rodando: boolean,
): string {
  // Enquanto a cobertura não fecha, o rodapé diz o que falta: botões
  // desabilitados sem motivo são a versão silenciosa do mesmo bloqueio.
  if (travado) {
    if (rodando) return ptBR.preAnalise.portaoAguardando;
    return ptBR.preAnalise.portaoIncompleto
      .replace('{triadas}', String(triadas))
      .replace('{total}', String(total));
  }
  if (triadas === 1) return ptBR.preAnalise.portaoResumoUma;
  return ptBR.preAnalise.portaoResumo.replace('{n}', String(triadas));
}

export function PortaoDoCaso({
  visivel,
  triadas,
  total,
  acaoEmCurso,
  travado,
  rodando,
  onAcao,
}: PortaoDoCasoProps): JSX.Element | null {
  if (!visivel) return null;
  const busy = acaoEmCurso !== null;
  const bloqueado = busy || travado;

  return (
    <section
      aria-labelledby="pre-analise-gate-title"
      className="mt-sc-8 flex flex-wrap items-end justify-between gap-sc-4 border-t border-border pt-sc-4"
      data-testid="pre-analise-gate"
    >
      <div className="min-w-0 max-w-prose">
        <h2 id="pre-analise-gate-title" className="text-heading-sm text-fg">
          {ptBR.preAnalise.gateTitle}
        </h2>
        <p className="mt-1 text-body-sm text-fg-muted">{ptBR.preAnalise.gateHelp}</p>
        <p className="mt-1 text-caption text-fg-dim" data-testid="pre-analise-gate-resumo">
          {resumoDoPortao(triadas, total, travado, rodando)}
        </p>
      </div>
      <div className="flex flex-col gap-sc-2 sm:flex-row" data-testid="pre-analise-ctas">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => onAcao('finalizar')}
          disabled={bloqueado}
          data-testid="pre-analise-cta-finalizar"
        >
          {acaoEmCurso === 'finalizar'
            ? ptBR.preAnalise.ctaFinalizando
            : ptBR.preAnalise.ctaFinalizar}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => onAcao('finalizar_com_relatorio')}
          disabled={bloqueado}
          data-testid="pre-analise-cta-relatorio"
        >
          {acaoEmCurso === 'finalizar_com_relatorio'
            ? ptBR.preAnalise.ctaGerandoRelatorio
            : ptBR.preAnalise.ctaGerarRelatorio}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => onAcao('seguir_mesa')}
          disabled={bloqueado}
          data-testid="pre-analise-cta-seguir"
        >
          {acaoEmCurso === 'seguir_mesa'
            ? ptBR.preAnalise.ctaSeguindo
            : ptBR.preAnalise.ctaSeguirMesa}
        </Button>
      </div>
    </section>
  );
}

export interface DesfechoComRelatorioProps {
  visivel: boolean;
  baixando: boolean;
  onBaixar: () => void;
}

/**
 * Estado PÓS-decisão do caso encerrado com relatório (E72.F2).
 *
 * Fica na tela enquanto o caso existir: o documento é re-renderizado sob demanda
 * (ADR-0074 §2.6), então baixar de novo — amanhã, de outra máquina — devolve o
 * mesmo PDF. Não há "gerar" aqui: gerar foi o ato que encerrou o caso.
 */
export function DesfechoComRelatorio({
  visivel,
  baixando,
  onBaixar,
}: DesfechoComRelatorioProps): JSX.Element | null {
  if (!visivel) return null;
  return (
    <section
      aria-labelledby="pre-analise-desfecho-title"
      className="mt-sc-8 flex flex-wrap items-end justify-between gap-sc-4 border-t border-border pt-sc-4"
      data-testid="pre-analise-desfecho-relatorio"
    >
      <div className="min-w-0 max-w-prose">
        <h2
          id="pre-analise-desfecho-title"
          className="flex items-center gap-sc-2 text-heading-sm text-fg"
        >
          <FileText className="h-[18px] w-[18px] shrink-0 stroke-[1.5] text-fg-muted" aria-hidden />
          {ptBR.preAnalise.desfechoRelatorioTitulo}
        </h2>
        <p className="mt-1 text-body-sm text-fg-muted">{ptBR.preAnalise.desfechoRelatorioTexto}</p>
      </div>
      <Button
        variant="primary"
        size="lg"
        onClick={onBaixar}
        disabled={baixando}
        data-testid="pre-analise-cta-baixar-relatorio"
      >
        <Download className="mr-sc-2 h-4 w-4" aria-hidden />
        {baixando ? ptBR.preAnalise.ctaBaixandoRelatorio : ptBR.preAnalise.ctaBaixarRelatorio}
      </Button>
    </section>
  );
}
